import { Dispatch, SetStateAction, useEffect, useState } from "react";
import BooleanField from "../../../../components/Form/BooleanField";
import SelectField from "../../../../components/Form/SelectFields";
import ConditionalSelectField from "../../../../components/Form/ConditionSelectField";
import StandardizeSMILES from "../CurateDSComponents/StandardizeSMILES";
import StandardizeBinaryValues from "../CurateDSComponents/StandardizeBinaryValues";
import Binarize from "../CurateDSComponents/Binarize";
import StandardizeRegression from "../CurateDSComponents/StandardizeRegression";
import StandardizeValues from "../CurateDSComponents/StandardizeValues";
import { ac_access } from "../../../../access/predefinedConditions";
import AccessGate from "../../../../components/Auth/AccessGate";
import { Skeleton } from "@mui/material";


export interface EcleanCuration {
    eclean_std_smiles: {
        original_smiles_column: string,
        remove_stereo: boolean,
    } | null,
    eclean_std_binary_values: {
        binary_value_column: string,
    } | null,
    eclean_std_values: {
        units_column: string,
        units_out: string,
        qualifier_column: string,
        value_column: string,
        value_range: {
            lower_bound: number | null,
            upper_bound: number | null,
        },
        kwargs_prune_duplicate_values: {
            sigfigs: number,
        } | null,
        kwargs_prune_population_duplicate_values: {
            min_num_duplicates: number,
            sigfigs: number,
        } | null,
        kwargs_prune_population_outliers: {
            iqr_fraction: number,
            iqr_min: number,
            keep_high_outliers: boolean,
            keep_low_outliers: boolean,
        },
        kwargs_prune_duplicate_outliers: {
            iqr_fraction: number,
            iqr_min: number,
            keep_high_outliers: boolean,
            keep_low_outliers: boolean,
        } | null,
        kwargs_prune_iqr_outliers: {
            iqr_fraction: number,
            iqr_min: number,
            iqr_max: number,
        } | null,
    } | null,
    eclean_std_binary: {
        binarization_threshold: number | null,
        binarization_qualifier: string | null,
        binarization_threshold_unit_conversion: {
            binarization_units: string,
            units_out: string,
        } | null,
        value_column: string | null,
        qualifier_column: string | null,
        binary_value_column: string | null,
        smiles_column: string | null,
    } | null,
    eclean_std_regression: {
        qualifier_column: string,
        value_column: string,
        smiles_column: string | null,
    } | null,
    input_units: string | null,
}


export const unitOptions = [
    { value: 'unitless', label: 'unitless' },
    { value: 'neglogM', label: 'neglogM' },
    { value: 'logM', label: 'logM' },
    { value: 'nmol/L', label: 'nmol/L' },
    { value: 'µmol/L', label: 'µmol/L' },
    { value: 'mmol/L', label: 'mmol/L' },
    { value: 'mol/L', label: 'mol/L' },
    { value: 'lnM', label: 'lnM' },
    { value: 'logS', label: 'logS' },
    { value: 'ng/L', label: 'ng/L' },
    { value: 'µg/10L', label: 'µg/10L' },
    { value: 'mg/L', label: 'mg/L' },
    { value: 'g/L', label: 'g/L' },
]

export const qualifierOptions = [
    // { value: 'eq', label: '=' }, // doesn't make sense here to include for binarization
    { value: 'lt', label: '≤' },
    { value: 'gt', label: '≥' }
]

const curationModelTypeOptions = [
    { value: 'continuous-continuous', label: 'Regression' },
    { value: 'continuous-single', label: 'Classification' },
]

export type CurationTypes = 'smiles' | 'single-class' | 'continuous-continuous' | 'continuous-single';


export default function Curate({
    isCurationTrue, 
    setIsCurationTrue,
    curationType,
    setCurationType,
    isLoading,
    fieldOptions,
    form, 
    setForm,
    activityColumns,
    stdSMILES,
    originalSMILES,
    menuPortalTargetId,
    showSelect,
    isSDF,
}: {
    isCurationTrue: boolean,
    setIsCurationTrue?: Dispatch<SetStateAction<boolean>>,
    curationType: CurationTypes,
    setCurationType: Dispatch<SetStateAction<CurationTypes>>,
    isLoading?: boolean,
    fieldOptions: {
        value: string,
        label: string
    }[],
    form: EcleanCuration,
    setForm: Dispatch<SetStateAction<EcleanCuration>>,
    activityColumns: {
        column: string,
        data_type: string,
    }[],
    stdSMILES: string | null,
    originalSMILES: string,
    menuPortalTargetId: string,
    showSelect: boolean,
    isSDF: boolean,
}) {
    const [ selectedActivityColumn, setSelectedActivityColumn ] = useState<{
        column: string,
        data_type: string,
    } | null>(null);

    useEffect(() => {
        let eclean_std_smiles = null;
        let eclean_std_binary_values = null;
        let eclean_std_values = null;
        let eclean_std_binary = null;
        let eclean_std_regression = null;

        // if the dataset has not standardized smiles column yet,
        // then we set some default parameters for eclean std smiles
        // in preparation for standardizing smiles in eclean
        //
        // NOTE: we need to use originalSMILES instead of just '' for SDF files
        // where the user can't change this and this is set by default to 'sdf_molblock'
        if (stdSMILES === null) {
            eclean_std_smiles = {
                original_smiles_column: originalSMILES,
                remove_stereo: false,
            }
        }

        if (curationType === 'single-class') {
            eclean_std_binary_values = {
                binary_value_column: selectedActivityColumn ? selectedActivityColumn.column : '',
            }

            eclean_std_binary = {
                binarization_threshold: null,
                binarization_qualifier: null,
                binarization_threshold_unit_conversion: null,
                value_column: null,
                qualifier_column: null,
                binary_value_column: selectedActivityColumn ? selectedActivityColumn.column : '',
                smiles_column: stdSMILES !== null ? stdSMILES : null,
            }
        }

        if (curationType.includes('continuous')) {
            eclean_std_values = {
                units_column: '',
                units_out: 'neglogM',
                qualifier_column: '',
                value_column: selectedActivityColumn ? selectedActivityColumn.column : '',
                value_range: {
                    lower_bound: null,
                    upper_bound: 12,
                },
                kwargs_prune_duplicate_values: null,
                kwargs_prune_population_duplicate_values: null,
                kwargs_prune_population_outliers: {
                    iqr_fraction: 1.5,
                    iqr_min: 2,
                    keep_high_outliers: false,
                    keep_low_outliers: false,
                },
                kwargs_prune_duplicate_outliers: null,
                kwargs_prune_iqr_outliers: null,
            }

            if (curationType === 'continuous-single') {
                eclean_std_binary = {
                    binarization_threshold: 1000,
                    binarization_qualifier: 'lt',
                    binarization_threshold_unit_conversion: {
                        binarization_units: 'neglogM',
                        units_out: 'neglogM',
                    },
                    value_column: selectedActivityColumn ? selectedActivityColumn.column : '',
                    qualifier_column: '',
                    binary_value_column: null,
                    smiles_column: stdSMILES !== null ? stdSMILES : null,
                }
            } else if (curationType === 'continuous-continuous') {
                eclean_std_regression = {
                    qualifier_column: '',
                    value_column: selectedActivityColumn ? selectedActivityColumn.column : '',
                    smiles_column: stdSMILES !== null ? stdSMILES : null,
                }
            }
        }

        setForm({
            eclean_std_smiles,
            eclean_std_binary_values,
            eclean_std_values,
            eclean_std_binary,
            eclean_std_regression,
            input_units: null,
        })

        //eslint-disable-next-line
    }, [stdSMILES, curationType, selectedActivityColumn]);
    

    const handleSelectActivityColumn = (e:any, name: string) => {
        const value = e.value;
        if (activityColumns.length > 0) {
            const match = activityColumns.find(col => col.column === value);
            if (match) {
                setSelectedActivityColumn(match);
                if (match.data_type === 'single-class-label') {
                    setCurationType('single-class')
                } else {
                    setCurationType('continuous-continuous');
                }
            } else {
                setSelectedActivityColumn(null);
                setCurationType('smiles');
            }
        }
    }

    const handleCurationModelType = (e:any, name: string) => {
        const value = e.value as any;

        setCurationType(value);
    }

    return (
        <>
            {setIsCurationTrue && (
                // Option to toggle curation if generating a ds from an existing file
                <div className="w-full flex justify-between items-end gap-4">
                    <div className="w-1/2">
                        <BooleanField 
                            label="Perform Data Curation"
                            id='perform-data-curation'
                            name='perform-data-curation'
                            value={isCurationTrue}
                            handleChange={(e: any) => setIsCurationTrue(e.target.checked)}
                            isRequired={false}
                        />
                    </div>
                </div>
            )}

            {isLoading !== undefined && isLoading ? (
                <>
                {Array.from(Array(4).keys()).map(key => (
                    <div className="flex flex-col gap-6 justify-start items-start w-full">
                        <div className="w-1/2 h-[20px]"><Skeleton variant='rounded' width='100%' height='100%'/></div>
                        <div className="w-full flex justify-between items-end gap-4">
                            <div className="w-1/2 h-[70px]"><Skeleton variant='rounded' width='100%' height='100%'/></div>
                            <div className="w-1/2 h-[70px]"><Skeleton variant='rounded' width='100%' height='100%'/></div>
                        </div>
                    </div>
                ))}
                </>
            ) : (
                <>
                {isCurationTrue && (
                    // If activity columns were selected, let user choose which one to curate (optional)
                <>
                    {activityColumns.length > 0 && (
                        <div className="w-full flex justify-between items-end gap-4">
                            <ConditionalSelectField label='Selected Activity Column' showSelect={showSelect}>
                                <SelectField 
                                    label="Selected Activity Column"
                                    id="selectedActivityColumn"
                                    name="selectedActivityColumn"
                                    value={selectedActivityColumn ? {
                                        value: selectedActivityColumn.column, label: selectedActivityColumn.column
                                    } : null}
                                    options={activityColumns.map(col => ({ value: col.column, label: col.column }))}
                                    isRequired={false}
                                    isCreatable={false}
                                    isMulti={false}
                                    handleChange={handleSelectActivityColumn}
                                    menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                                />
                            </ConditionalSelectField> 
                            <div className="w-full flex flex-col justify-center items-start gap-2 text-default">
                                <label className={`text-[12px] font-medium`}>Original Data Type</label>
                                <input disabled placeholder="Data Type" value={selectedActivityColumn ? selectedActivityColumn.data_type : ''} className="px-4 py-3 rounded-lg border border-outline focus:border-primary focus:outline-none w-full"/>
                            </div>
                        </div>
                    )}

                    {selectedActivityColumn && selectedActivityColumn.data_type === 'continuous-value' && (
                        // If selected activity column is continuous, let user choose intended model type
                        <div className="w-full">
                            <ConditionalSelectField label='Curation Model Type' showSelect={showSelect}>
                                <SelectField 
                                    label="Curation Model Type"
                                    id="curationModelType"
                                    name="curationModelType"
                                    value={curationType.includes('continuous') ? curationModelTypeOptions.find(opt => opt.value === curationType)! : null}
                                    options={curationModelTypeOptions}
                                    isRequired={false}
                                    isCreatable={false}
                                    isMulti={false}
                                    handleChange={handleCurationModelType}
                                    menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                                />
                            </ConditionalSelectField> 
                        </div>
                    )}

                    <StandardizeSMILES 
                        form={form}
                        setForm={setForm}
                        fieldOptions={fieldOptions}
                        menuPortalTargetId={menuPortalTargetId}
                        showSelect={showSelect}
                        isSDF={isSDF}
                    />
                    <AccessGate
                        or={ac_access}
                    >
                        <StandardizeBinaryValues 
                            form={form}
                            setForm={setForm}
                            fieldOptions={fieldOptions}
                            menuPortalTargetId={menuPortalTargetId}
                            showSelect={showSelect}
                        />

                        <StandardizeValues 
                            form={form}
                            setForm={setForm}
                            fieldOptions={fieldOptions}
                            menuPortalTargetId={menuPortalTargetId}
                            showSelect={showSelect}
                        />

                        <Binarize 
                            form={form}
                            setForm={setForm}
                            fieldOptions={fieldOptions}
                            menuPortalTargetId={menuPortalTargetId}
                            showSelect={showSelect}
                        />

                        <StandardizeRegression 
                            form={form}
                            setForm={setForm}
                            fieldOptions={fieldOptions}
                            menuPortalTargetId={menuPortalTargetId}
                            showSelect={showSelect}
                        />
                    </AccessGate>

                </>
                )}
                </>
            )}

            

        </>
    )
}